import { MaterialTableProps } from "@material-table/core";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { TablePagination, TablePaginationProps } from "@mui/material";
import { useTheme } from "@mui/styles";
import { Map } from "ol";
import React from "react";
import { useTranslation } from "react-i18next";
import { LuodeMeasurementDto } from "~/api/GeneratedApi";
import GenericTable, { CustomColumn } from "~/components/GenericTable";
import { useDateFormatter } from "~/utils/hooks/useDateFormatter";
import { LuodeMapItem } from "../types/mapItems";
import { GenericPopup } from "./GenericPopup";

type Props = {
  map: Map;
} & (ClickProps | HoverProps);

type ClickProps = {
  clickData: LuodeMapItem;
  hoverData?: LuodeMapItem;
};

type HoverProps = {
  hoverData: LuodeMapItem;
  clickData?: LuodeMapItem;
};

export const LuodePopup = ({
  hoverData,
  clickData,
  map,
}: Props): JSX.Element | null => {
  const { t } = useTranslation("conditions");
  const dateFormatter = useDateFormatter();
  const theme = useTheme();

  const data = clickData ?? hoverData;

  const tableHeight = 200;

  const checkIfColumnHasData = React.useCallback(
    (
      column: CustomColumn<LuodeMeasurementDto>,
    ): CustomColumn<LuodeMeasurementDto>[] | [] => {
      const meas = data?.measurements ?? [];
      if (
        meas.some(
          (m) =>
            m[column.field! as keyof LuodeMeasurementDto] !== undefined &&
            m[column.field! as keyof LuodeMeasurementDto] !== null,
        )
      )
        return [column];
      return [];
    },
    [data?.measurements],
  );

  const columns = React.useMemo<CustomColumn<LuodeMeasurementDto>[]>(
    () => [
      {
        title: t("timestamp"),
        field: "time",
        defaultSort: "desc",
        render: ({ time }) => (time ? dateFormatter({ date: time }) : ""),
      },
      ...checkIfColumnHasData({
        title: t("water-level"),
        field: "waterLevel",
      }),
      ...checkIfColumnHasData({
        title: t("temperature"),
        field: "temperature",
      }),
      ...checkIfColumnHasData({
        title: t("direction"),
        field: "direction",
        render: ({ direction }) => (
          <ArrowDownwardIcon
            style={{
              transform: `rotate(${direction}deg)`,
              fontSize: "inherit",
            }}
          />
        ),
      }),
      ...checkIfColumnHasData({
        title: t("speed"),
        field: "speed",
      }),
      ...checkIfColumnHasData({
        title: t("battery"),
        field: "battery",
      }),
      ...checkIfColumnHasData({
        title: t("flow-speed-west"),
        field: "flowSpeedWest",
      }),
      ...checkIfColumnHasData({
        title: t("flow-speed-middle"),
        field: "flowSpeedMiddle",
      }),
      ...checkIfColumnHasData({
        title: t("flow-speed-east"),
        field: "flowSpeedEast",
      }),
      ...checkIfColumnHasData({
        title: t("north-cell-direction"),
        field: "northCellDirection",
        render: ({ northCellDirection }) => (
          <ArrowDownwardIcon
            style={{
              transform: `rotate(${northCellDirection}deg)`,
              fontSize: "inherit",
            }}
          />
        ),
      }),
      ...checkIfColumnHasData({
        title: t("north-cell-speed"),
        field: "northCellSpeed",
      }),
      ...checkIfColumnHasData({
        title: t("middle-cell-direction"),
        field: "middleCellDirection",
        render: ({ middleCellDirection }) => (
          <ArrowDownwardIcon
            style={{
              transform: `rotate(${middleCellDirection}deg)`,
              fontSize: "inherit",
            }}
          />
        ),
      }),
      ...checkIfColumnHasData({
        title: t("middle-cell-speed"),
        field: "middleCellSpeed",
      }),
      ...checkIfColumnHasData({
        title: t("south-cell-direction"),
        field: "southCellDirection",
        render: ({ southCellDirection }) => (
          <ArrowDownwardIcon
            style={{
              transform: `rotate(${southCellDirection}deg)`,
              fontSize: "inherit",
            }}
          />
        ),
      }),
      ...checkIfColumnHasData({
        title: t("south-cell-speed"),
        field: "southCellSpeed",
      }),
    ],
    [checkIfColumnHasData, dateFormatter, t],
  );

  const tableProps: Partial<MaterialTableProps<LuodeMeasurementDto>> =
    React.useMemo(
      () => ({
        title: "",
        options: {
          padding: "dense",
          maxBodyHeight: `${tableHeight}px`,
          minBodyHeight: `${tableHeight}px`,
          pageSizeOptions: [5, 10, 15, 25, 50],
          pageSize: 10,
          toolbar: false,
          headerStyle: { fontSize: 14 },
          rowStyle: { fontSize: 13 },
        },
        components: {
          Container: (props) => (
            <div
              {...props}
              style={{
                borderTopLeftRadius: 0,
                borderBottomLeftRadius: 0,
                border: "none",
                boxShadow: "none",
              }}
            />
          ),
          Pagination: (props: TablePaginationProps) => {
            const { style } = props;
            return (
              <TablePagination
                {...props}
                style={{
                  borderBottomRightRadius: theme.shape.borderRadius,
                  ...style,
                }}
              />
            );
          },
        },
      }),
      [tableHeight, theme.shape.borderRadius],
    );

  const measTable = React.useMemo(
    () => (
      <GenericTable<LuodeMeasurementDto>
        columns={columns}
        data={data?.measurements ?? []}
        tableProps={tableProps}
      />
    ),
    [columns, data?.measurements, tableProps],
  );

  if (!data) return null;

  const { id, name } = data;

  return (
    <GenericPopup
      map={map}
      clicked={!!clickData}
      avatarText={id.toString()}
      title={name}
      subheader=""
      content={measTable}
    />
  );
};
